// How fresh the data the modules watch is (the market tables, per bar size for the candles) on the System overview:
// NextAdmin `tables/basic-tables` in a card. From the coverage snapshot; a row past its cadence is flagged.
import { Badge, Card, DataTable, type DataTableColumn } from '../../../components/ui/index.js';
import type { SystemDataFreshness } from '../../../generated/api/types.gen.js';
import { formatAgo, formatCount, formatSeconds } from './metric-format.js';

const stale = (row: SystemDataFreshness) =>
  row.ageSeconds !== null && row.staleAfterSeconds !== null && row.ageSeconds > row.staleAfterSeconds;

function FreshnessState({ row }: { row: SystemDataFreshness }) {
  if (row.newestAt === null) return <Badge variant="neutral">Empty</Badge>;
  if (row.staleAfterSeconds === null) return <Badge variant="neutral">No cadence</Badge>;
  if (stale(row)) {
    return (
      <Badge variant={(row.ageSeconds ?? 0) > 4 * row.staleAfterSeconds ? 'danger' : 'warning'} dot>
        Stale
      </Badge>
    );
  }
  return (
    <Badge variant="success" dot>
      Fresh
    </Badge>
  );
}

const COLUMNS: DataTableColumn<SystemDataFreshness>[] = [
  { key: 'source', header: 'Source', render: (r) => <span className="font-mono text-xs">{r.source}</span> },
  { key: 'part', header: 'Bar size', render: (r) => r.part ?? '—' },
  { key: 'state', header: 'State', render: (r) => <FreshnessState row={r} /> },
  {
    key: 'newest',
    header: 'Newest row',
    sortValue: (r) => (r.newestAt ? Date.parse(r.newestAt) : null),
    render: (r) => r.newestAt?.slice(0, 16).replace('T', ' ') ?? '—',
  },
  {
    key: 'age',
    header: 'Age',
    align: 'right',
    sortValue: (r) => r.ageSeconds,
    render: (r) => formatSeconds(r.ageSeconds),
  },
  {
    key: 'expected',
    header: 'Expected within',
    align: 'right',
    render: (r) => formatSeconds(r.staleAfterSeconds),
  },
  { key: 'series', header: 'Series', align: 'right', sortValue: (r) => r.series, render: (r) => formatCount(r.series) },
];

export function DataFreshnessCard({ rows, nowMs }: { rows: SystemDataFreshness[]; nowMs: number }) {
  const checkedAt = rows.find((row) => row.checkedAt)?.checkedAt ?? null;
  return (
    <div id="data-freshness" className="xl:col-span-12">
      <Card title="Data freshness">
        <p className="mb-3 text-xs text-muted">{`Snapshot computed ${formatAgo(checkedAt, nowMs)}.`}</p>
        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={(r) => `${r.source} ${r.part ?? ''}`}
          getRowAttention={(r) => (stale(r) ? 'warning' : undefined)}
          empty={
            <p className="py-6 text-center text-sm text-muted">
              No module reports data freshness in this environment yet.
            </p>
          }
        />
      </Card>
    </div>
  );
}
